import React, { useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import UserContext from "../assets/user_context";
import * as ROUTES from "../constants/routes";
import styled from "@emotion/styled";
import { useThemeMode } from "../theme/ThemeContext";
import LoadingLogo from "../assets/logos/animated-logo.svg?react";
import LoadingLogoLight from "../assets/logos/animated-logo-light.svg?react";

const LoadingScreenDiv = styled.div`
  display: flex;
  position: absolute;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
  justify-content: center;
  align-items: center;
  z-index: 3;
`;

const LoadingScreenOverlay = styled.div`
  display: flex;
  position: absolute;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
  background-color: ${(props) => (props.isDark ? "#121212" : "white")};
  justify-content: center;
  align-items: center;
  z-index: 2;
  opacity: 0.6;
  backdrop-filter: blur(15px);
`;

const LoadingLogoStyled = styled.div`
  z-index: 5;
  transform: translateY(-5vh);
  width: 17vw;

  svg {
    width: 100%;
    height: auto;
  }

  @media only screen and (max-width: 2500px) {
    width: 20vw;
  }
  @media only screen and (max-width: 1500px) {
    width: 25vw;
  }
  @media only screen and (max-width: 1500px) {
    width: 30vw;
  }
  @media only screen and (max-width: 1200px) {
    width: 35vw;
  }
  @media only screen and (max-width: 1000px) {
    width: 40vw;
  }
  @media only screen and (max-width: 800px) {
    width: 45vw;
  }
  @media only screen and (max-width: 650px) {
    width: 55vw;
  }
  @media only screen and (max-width: 450px) {
    width: 80vw;
  }
`;

const LoadingText = styled.div`
  z-index: 5;
  font-size: 1.3rem;
  font-weight: 500;
  color: ${(props) => (props.isDark ? "#ffffff" : "#333333")};
  margin-top: 2rem;
  text-align: center;
  font-family: "MontserratMed", sans-serif;
`;

/**
 * LoadingGuard component that prevents rendering children until auth and data are loaded
 * Also handles redirects for unauthenticated users
 */
export const LoadingGuard = ({
  requireUser = true,
  requireOrg = true,
  children,
}) => {
  const {
    user,
    loaded,
    userStillLoading,
    org,
    orgData,
    inviteCheckState,
    userData,
  } = useContext(UserContext);

  const { mode } = useThemeMode();
  const isDark = mode === "dark";

  const navigate = useNavigate();
  const location = useLocation();

  // Store the intended destination in sessionStorage
  useEffect(() => {
    if (requireUser && !user && !userStillLoading) {
      // Store current path to redirect back after login
      sessionStorage.setItem(
        "redirectAfterLogin",
        location.pathname + location.search,
      );
      navigate(ROUTES.SIGN_IN);
    }
  }, [user, userStillLoading, requireUser, navigate, location]);

  // Force users flagged with a temporary password to set a new one.
  // Soft redirect: a user can manually navigate elsewhere, but any protected
  // route re-runs this and sends them back until the flag is cleared.
  useEffect(() => {
    // After a successful change the page sets this sentinel so the user isn't
    // bounced back here in the brief window before userData reflects the
    // cleared flag.
    if (sessionStorage.getItem("passwordJustChanged")) {
      sessionStorage.removeItem("passwordJustChanged");
      return;
    }
    if (
      requireUser &&
      user &&
      userData?.mustChangePassword === true &&
      location.pathname !== ROUTES.SET_NEW_PASSWORD
    ) {
      navigate(ROUTES.SET_NEW_PASSWORD);
    }
  }, [requireUser, user, userData, location.pathname, navigate]);

  // Check if we should show loading
  const shouldShowLoading = () => {
    // Still setting up Firebase connection
    if (!loaded) return true;

    // Loading user state
    if (userStillLoading) return true;

    // Need user but no user
    if (requireUser && !user) return true;

    // NEW: If we have a user but userData isn't loaded yet, keep loading
    // This prevents crashes from components trying to access userData.username, etc.
    if (requireUser && user && !userData) {
      return true;
    }

    // Need org but no org/org data
    if (requireOrg && (!org || !orgData)) return true;

    return false;
  };

  // Get contextual loading message
  const getLoadingMessage = () => {
    if (userStillLoading) return "Loading authentication...";

    // NEW: Add message for userData loading
    if (user && !userData) return "Loading user profile...";

    // Check if user has no orgs and we're dealing with invites
    if (user && (!org || !orgData)) {
      if (inviteCheckState?.checking) {
        return "Checking for organization invites...";
      }
      if (inviteCheckState?.waiting) {
        return "Organization found! Adding you...";
      }
      if (inviteCheckState?.checked && !inviteCheckState?.found) {
        return "Loading organization setup...";
      }
    }

    if (!org) return "Loading organization...";
    if (!orgData) return "Loading organization data...";
    return "Loading...";
  };

  if (shouldShowLoading()) {
    return (
      <LoadingScreenDiv>
        <LoadingLogoStyled>
          {isDark ? <LoadingLogoLight /> : <LoadingLogo />}
        </LoadingLogoStyled>
        <LoadingText isDark={isDark}>{getLoadingMessage()}</LoadingText>
        <LoadingScreenOverlay isDark={isDark} />
      </LoadingScreenDiv>
    );
  }

  // If auth is required but no user after loading, return null (redirect will happen)
  if (requireUser && !user) {
    return null;
  }

  return <>{children}</>;
};

export default LoadingGuard;
